import { useLazyQuery, useQuery } from "@apollo/client";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useImperativeHandle, forwardRef, useMemo } from "react";
import { usePrevious } from "react-use";
import { supplyChainBookingFieldSettingsQuery } from "../BookingForm";
import { type MetadataField } from "../BookingForm/types";
import { BookingStatusBadge } from "../BookingStatusBadge";
import { BookingsFilters, statusOptions } from "./BookingsFilters";
import { metadataColumns } from "./columnDefinitions";
import { FilterBadge } from "@/benchmarks/BenchmarkFilters/FilterBadges";
import { useUrlTableSorting } from "@/components/DataTable/useUrlTableSorting";
import {
  pageInfoFragment,
  Pagination,
  type PaginationProperties,
  type ResetPagination,
  usePagination,
} from "@/components/Pagination";
import { Table } from "@/components/Table";
import { Heading4 } from "@/components/Typography";
import { Card } from "@/components/ui/card";
import { ToastAction } from "@/components/ui/toast";
import { graphql, type VariablesOf, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { usePageSizePreference } from "@/hooks/use-page-size-preference";
import { useSortPreference } from "@/hooks/use-sort-preference";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { toDisplayDate } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatName } from "@/lib/utils/formatting";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

export type SortingField = NonNullable<NonNullable<VariablesOf<typeof supplyChainBookingsQuery>["sortBy"]>[0]>["field"];

export const BOOKINGS_SORT_FIELDS = [
  "NAME",
  "PLANNED_SHIPPING_DATE",
  "STATUS",
  "CREATED_AT",
] as const satisfies readonly SortingField[];

export type BookingsSortField = (typeof BOOKINGS_SORT_FIELDS)[number];

interface BookingsTableProps {
  filters: BookingFilters;
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
}

export interface BookingsTableHandle {
  resetPagination: ResetPagination;
}

export type SupplyChainBooking = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof supplyChainBookingsQuery>["supplyChainBookings"]>["edges"]>[number]
>["node"];

const { accessor } = createColumnHelper<SupplyChainBooking>();
const routeApi = getRouteApi("/(authed)/_layout/bookings");

export const BookingsTable = forwardRef<BookingsTableHandle, BookingsTableProps>(({ filters, setFilters }, ref) => {
  const navigate = routeApi.useNavigate();

  const [getBookings, { loading, data, error, refetch }] = useLazyQuery(supplyChainBookingsQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { dateOpenedAfter, dateOpenedBefore, status, sortField, sortDesc, pageSize /*, name, lotNumber */ } =
    routeApi.useSearch();
  const [pageSizePreference, setPageSizePreference] = usePageSizePreference("bookingsTable");
  const [sortPreference, setSortPreference] = useSortPreference("bookingsTable");

  // Fetch field settings to determine which metadata columns to show
  const { data: fieldSettingsData } = useQuery(supplyChainBookingFieldSettingsQuery, {
    variables: { id: activeBusinessUnitId || "" },
    skip: !activeBusinessUnitId,
  });

  const fieldSettings = removeNothings(fieldSettingsData?.supplyChainBookingFieldSettings || []);

  // const lotNumberSetting = fieldSettings.find((field) => field.field === "LOT_NUMBER");
  // const usesLotNumber = lotNumberSetting?.isVisible ?? false;

  const pagination = usePagination({
    totalCount: data?.supplyChainBookings?.numSupplyChainBookings || 0,
    pageInfo: data?.supplyChainBookings?.pageInfo,
    loading,
    urlPageSize: pageSize,
    localStoragePageSize: pageSizePreference,
    onUrlPageSizeChange: (newPageSize) => {
      void navigate({ search: (prev) => ({ ...prev, pageSize: newPageSize }) });
    },
    onPageSizePreferenceChange: setPageSizePreference,
  });

  useImperativeHandle(ref, () => ({
    resetPagination: pagination.controls.handleResetPagination,
  }));

  const { sortBy, onSortingChange, sorting } = useUrlTableSorting<BookingsSortField>({
    initialSort: { id: "PLANNED_SHIPPING_DATE", desc: true },
    validFields: BOOKINGS_SORT_FIELDS,
    sortParams: { sortField, sortDesc },
    localStorageSort: sortPreference,
    onSortParamsChange: (field, desc) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          sortField: field,
          sortDesc: desc,
        }),
      });
    },
    onLocalStorageSortChange: setSortPreference,
  });

  const bookings = removeNothings(
    data?.supplyChainBookings?.edges.map((edge) => {
      return edge?.node;
    }) || [],
  );

  useEffect(() => {
    const variables: VariablesOf<typeof supplyChainBookingsQuery> = {
      first: pagination.paginationQueryVariables.first,
      last: pagination.paginationQueryVariables.last,
      after: pagination.paginationQueryVariables.after,
      before: pagination.paginationQueryVariables.before,
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
      sortBy,
      plannedShippingDateBefore: dateOpenedBefore,
      plannedShippingDateAfter: dateOpenedAfter,
      status,
      // name,
      // lotNumber,
    };

    const fetchData = async () => {
      await getBookings({
        variables,
      });
    };

    fetchData().catch((error: unknown) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
        action: (
          <ToastAction onClick={() => refetch()} altText="Try again">
            Try again
          </ToastAction>
        ),
      });
    });
  }, [
    pagination.paginationQueryVariables.first,
    pagination.paginationQueryVariables.last,
    pagination.paginationQueryVariables.after,
    pagination.paginationQueryVariables.before,
    getBookings,
    toast,
    refetch,
    activeBusinessUnitId,
    sortBy,
    dateOpenedBefore,
    dateOpenedAfter,
    status,
    // name,
    // lotNumber,
  ]);

  const prevNumBookings = usePrevious(data?.supplyChainBookings?.numSupplyChainBookings);
  const displayNumBookings =
    loading && prevNumBookings ? prevNumBookings : data?.supplyChainBookings?.numSupplyChainBookings || 0;

  const columns = useBookingTableColumns(fieldSettings);

  return (
    <Card className={"m-4 p-4 bg-transparent overflow-hidden"}>
      <Heading4>Bookings ({displayNumBookings})</Heading4>
      <Table.Root
        data={bookings}
        columns={columns}
        loading={loading && !data}
        error={error?.message}
        tableId="bookingsTable"
        sortConfig={{ onSortingChange, sorting }}
      >
        <Table.Header className="px-0">
          <FilterBadges
            handleResetPagination={pagination.controls.handleResetPagination}
            setFilters={setFilters}
            filters={filters}
          />
          <Table.ColumnToggle />
          <BookingsFilters filters={filters} setFilters={setFilters} /*usesLotNumber={usesLotNumber}*/ />
        </Table.Header>

        <Table.Body<SupplyChainBooking>
          onRowClick={(row) => {
            if (!row) return;

            void navigate({
              to: "/bookings/$bookingId",
              params: { bookingId: row.id },
            });
          }}
          pageSize={Number(pagination.paginationInfo.pageSize)}
        />

        <Table.Footer className="flex justify-end">
          <Pagination pagination={pagination} />
        </Table.Footer>
      </Table.Root>
    </Card>
  );
});

BookingsTable.displayName = "BookingsTable";

function useBookingTableColumns(
  fieldSettings: Array<{ field: MetadataField; isVisible: boolean; isRequired: boolean }>,
) {
  const visibilityMap = useMemo(
    () => new Map(fieldSettings.map((setting) => [setting.field, setting.isVisible])),
    [fieldSettings],
  );

  return useMemo(() => {
    const baseColumns = [
      accessor((row) => row?.name, {
        id: "NAME" satisfies BookingsSortField,
        header: "Booking Name",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      accessor((row) => row?.plannedShippingDate, {
        id: "PLANNED_SHIPPING_DATE" satisfies BookingsSortField,
        header: "Planned Shipping Date",
        cell: (info) => {
          const plannedShippingDate = info.getValue();
          return plannedShippingDate ? toDisplayDate(plannedShippingDate) : "\u2014";
        },
        enableSorting: true,
      }),
      accessor((row) => row?.createdAt, {
        id: "CREATED_AT" satisfies BookingsSortField,
        header: "Date Created",
        cell: (info) => {
          const createdAt = info.getValue();
          return createdAt ? toDisplayDate(createdAt) : "\u2014";
        },
        enableSorting: true,
      }),
      accessor((row) => row?.accountManager, {
        id: "BOOKING_MANAGER",
        header: "Booking Manager",
        cell: (info) => {
          const accountManager = info.getValue();
          if (!accountManager?.user) return "\u2014";
          return formatName({
            firstName: accountManager.user.firstName,
            lastName: accountManager.user.lastName,
          });
        },
        enableSorting: false,
      }),
      accessor((row) => row?.numberOfHead, {
        id: "numberOfHead",
        header: "Number of Head",
        cell: (info) => {
          const numberOfHead = info.getValue();
          return numberOfHead ? numberOfHead.toString() : "\u2014";
        },
      }),
      accessor((row) => row?.source, {
        id: "source",
        header: "Source",
        cell: (info) => {
          const source = info.getValue();
          if (!source) return "\u2014";
          return source.supplyChainPartner?.businessName || "\u2014";
        },
      }),
      accessor((row) => row?.destination, {
        id: "destination",
        header: "Destination",
        cell: (info) => {
          const destination = info.getValue();
          if (!destination) return "\u2014";
          return destination.supplyChainPartner?.businessName || "\u2014";
        },
      }),
      accessor((row) => row?.status, {
        id: "STATUS" satisfies BookingsSortField,
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          return status ? <BookingStatusBadge status={status} /> : "\u2014";
        },
        enableSorting: true,
      }),
    ];

    // Filter metadata columns based on visibility settings
    const visibleMetadataColumns = metadataColumns.filter(
      (col) => visibilityMap.get(col.id as MetadataField) !== false,
    );

    return [...baseColumns, ...visibleMetadataColumns];
  }, [visibilityMap]);
}

function FilterBadges({
  filters,
  setFilters,
  handleResetPagination,
}: {
  filters: BookingFilters;
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  handleResetPagination: PaginationProperties["controls"]["handleResetPagination"];
}) {
  const navigate = useNavigate({ from: "/bookings/" });

  // const handleRemoveName = () => {
  //   setFilters((prevFilters) => ({ ...prevFilters, name: undefined }));
  //   handleResetPagination();
  //   void navigate({ search: (prev) => ({ ...prev, name: undefined }) });
  // };

  // const handleRemoveLotNumber = () => {
  //   setFilters((prevFilters) => ({ ...prevFilters, lotNumber: undefined }));
  //   handleResetPagination();
  //   void navigate({ search: (prev) => ({ ...prev, lotNumber: undefined }) });
  // };

  const handleRemoveDateRange = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      dateOpenedAfter: undefined,
      dateOpenedBefore: undefined,
    }));

    handleResetPagination();

    void navigate({ search: (prev) => ({ ...prev, dateOpenedAfter: undefined, dateOpenedBefore: undefined }) });
  };

  const handleRemoveStatus = (statusToRemove: string) => {
    const updatedStatus = filters.status?.filter((s) => s !== statusToRemove);

    setFilters((prevFilters) => ({
      ...prevFilters,
      status: updatedStatus,
    }));

    handleResetPagination();

    void navigate({
      search: (prev) => ({
        ...prev,
        status: updatedStatus?.length ? updatedStatus : undefined,
      }),
    });
  };

  return (
    <div className="min-h-11 flex items-center gap-2 pt-4 flex-wrap">
      {/* {filters.name ? (
        <FilterBadge enabled label={`Booking Name: ${filters.name}`} onRemove={() => handleRemoveName()} />
      ) : null}
      {filters.lotNumber ? (
        <FilterBadge enabled label={`Lot No.: ${filters.lotNumber}`} onRemove={() => handleRemoveLotNumber()} />
      ) : null} */}
      {filters.dateOpenedAfter && filters.dateOpenedBefore ? (
        <FilterBadge
          enabled
          label={`${toDisplayDate(filters.dateOpenedAfter, "MMM d")} - ${toDisplayDate(filters.dateOpenedBefore, "MMM d")}`}
          onRemove={handleRemoveDateRange}
        />
      ) : null}
      {filters.status?.map((statusValue) => {
        const statusOption = statusOptions.find((opt) => opt.value === statusValue);
        return statusOption ? (
          <FilterBadge
            key={statusValue}
            enabled
            label={statusOption.label}
            onRemove={() => handleRemoveStatus(statusValue)}
          />
        ) : null;
      })}
    </div>
  );
}

export const supplyChainBookingsQuery = graphql(
  `
    query SupplyChainBookings(
      $first: Int
      $last: Int
      $before: String
      $after: String
      $businessUnitIds: [ID!]
      $sortBy: [SupplyChainBookingsColumnSort!]
      $plannedShippingDateBefore: Date
      $plannedShippingDateAfter: Date
      $status: [SupplyChainBookingStatusEnumType!]
    ) {
      supplyChainBookings(
        first: $first
        last: $last
        before: $before
        after: $after
        owners: $businessUnitIds
        sortBy: $sortBy
        plannedShippingDateBefore: $plannedShippingDateBefore
        plannedShippingDateAfter: $plannedShippingDateAfter
        status: $status
      ) {
        numSupplyChainBookings
        pageInfo {
          ...PageInfo
        }
        edges {
          node {
            id
            name
            plannedShippingDate
            numberOfHead
            status
            createdAt
            source {
              id
              supplyChainPartner {
                id
                businessName
              }
              address {
                city
                state
              }
            }
            destination {
              id
              supplyChainPartner {
                id
                businessName
              }
              address {
                city
                state
              }
            }
            accountManager {
              user {
                id
                firstName
                lastName
              }
            }
            # Metadata fields
            lotNumber
            penNumber
            accountNumber
            sexClass
            confirmed
            accreditations {
              id
              status
              label
            }
            labels {
              id
              status
              label
            }
            condition
            conditions
            ownershipType
            breedNote
            dateOfSale
            dateOfVisit
            projectedOutDate
            dateDelivered
            price
            freightCost
            slide
            purchasePricePerCwt
            riskManagement
            shippedWeight
            baseWeight
            estimatedChoicePercent
            shrinkPercent
            estimatedWeightNote
            sourceNote
            brandNote
            certificateNumber
            program {
              id
              label
            }
            grid {
              id
              label
            }
            truckingCompany {
              id
              businessName
            }
            broker {
              id
              businessName
            }
            veterinarian {
              id
              user {
                id
                firstName
                lastName
              }
            }
            serviceProvided
          }
        }
      }
    }
  `,
  [pageInfoFragment],
);
